'use client'

import { useState } from 'react'
import { Button, Pip, StripeTag, Text } from '@/design-system'
import type { UserTier } from '@/types/transfers'
import type { BillingPeriod } from '@/lib/billing-price-ids'

interface PricingPlan {
  tier: UserTier
  name: string
  monthlyPrice: string
  annualPrice: string
  monthlyPeriodText: string
  annualPeriodText: string
  description: string
  features: string[]
  cta: string
  highlighted?: boolean
}

const plans: PricingPlan[] = [
  {
    tier: 'free',
    name: 'Free',
    monthlyPrice: '$0',
    annualPrice: '$0',
    monthlyPeriodText: 'forever',
    annualPeriodText: 'forever',
    description: 'For occasional file sharing',
    features: [
      'Up to 3 GB per transfer',
      '5 active transfers',
      '7-day link expiration',
      '10 recipients per transfer',
      'Security scanning'
    ],
    cta: 'Get started'
  },
  {
    tier: 'pro',
    name: 'Pro',
    monthlyPrice: '$10',
    annualPrice: '$8',
    monthlyPeriodText: '/month',
    annualPeriodText: '/mo · billed annually',
    description: 'For professionals and teams',
    features: [
      '250 GB per transfer',
      '50 active transfers',
      '30-day link expiration',
      'Password-protected transfers',
      'Download notifications',
      'Scheduled sends'
    ],
    cta: 'Upgrade to Pro',
    highlighted: true
  },
  {
    tier: 'business',
    name: 'Business',
    monthlyPrice: '$25',
    annualPrice: '$20',
    monthlyPeriodText: '/month',
    annualPeriodText: '/mo · billed annually',
    description: 'For organizations at scale',
    features: [
      'Unlimited (fair use) per transfer',
      'Unlimited active transfers',
      '90-day link expiration',
      'Password-protected transfers',
      'Download notifications',
      'Custom branding & subdomain',
      'Client proofing',
      'One flat rate'
    ],
    cta: 'Upgrade to Business'
  }
]

interface PricingTableProps {
  currentTier?: UserTier
  billingEnabled?: boolean
  onSelectPlan?: (tier: UserTier, period: BillingPeriod) => void
  /** Show "Sign Up" CTAs instead of "Upgrade" (for marketing page) */
  signUpMode?: boolean
  /** Called when a sign-up CTA is clicked in signUpMode */
  onSignUp?: () => void
}

function Check() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 16,
        height: 16,
        flexShrink: 0,
        position: 'relative',
        marginTop: 2
      }}>
      <span
        style={{
          position: 'absolute',
          left: 2,
          top: -1,
          width: 6,
          height: 11,
          borderRight: 'var(--border-w) solid var(--ink)',
          borderBottom: 'var(--border-w) solid var(--ink)',
          transform: 'rotate(45deg)'
        }}
      />
    </span>
  )
}

export function PricingTable({
  currentTier,
  billingEnabled = false,
  onSelectPlan,
  signUpMode = false,
  onSignUp
}: PricingTableProps) {
  const [loadingTier, setLoadingTier] = useState<UserTier | null>(null)
  const [period, setPeriod] = useState<BillingPeriod>('annual')

  const handleSelect = async (tier: UserTier) => {
    if (onSelectPlan) {
      setLoadingTier(tier)
      onSelectPlan(tier, period)
      setLoadingTier(null)
    }
  }

  return (
    <div>
      {/* Monthly / Annual toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          marginBottom: 24
        }}>
        <Button
          variant={period === 'monthly' ? 'primary' : 'default'}
          onClick={() => setPeriod('monthly')}
          style={{ minWidth: 100 }}>
          Monthly
        </Button>
        <Button
          variant={period === 'annual' ? 'primary' : 'default'}
          onClick={() => setPeriod('annual')}
          style={{ minWidth: 100 }}>
          Annual
          <Text
            variant="mono-micro"
            as="span"
            style={{ marginLeft: 6, opacity: 0.75, fontSize: 10, letterSpacing: '0.1em' }}>
            −20%
          </Text>
        </Button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 18,
          maxWidth: 1080,
          margin: '0 auto'
        }}>
        {plans.map((plan) => (
          <PricingPlanCard
            key={plan.tier}
            plan={plan}
            period={period}
            currentTier={currentTier}
            billingEnabled={billingEnabled}
            signUpMode={signUpMode}
            onSignUp={onSignUp}
            loading={loadingTier === plan.tier}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </div>
  )
}

interface PricingPlanCardProps {
  plan: PricingPlan
  period: BillingPeriod
  currentTier?: UserTier
  billingEnabled: boolean
  signUpMode: boolean
  onSignUp?: () => void
  loading: boolean
  onSelect: (tier: UserTier) => void
}

function PricingPlanCard({
  plan,
  period,
  currentTier,
  billingEnabled,
  signUpMode,
  onSignUp,
  loading,
  onSelect
}: PricingPlanCardProps) {
  const isCurrent = currentTier === plan.tier
  const isPaidUser = currentTier && currentTier !== 'free'
  const isFreePlanCard = plan.tier === 'free'
  const isDowngrade = isPaidUser && isFreePlanCard

  const displayPrice = period === 'annual' ? plan.annualPrice : plan.monthlyPrice
  const displayPeriodText = period === 'annual' ? plan.annualPeriodText : plan.monthlyPeriodText

  return (
    <div
      style={{
        position: 'relative',
        border: plan.highlighted ? 'var(--border-lg) solid var(--ink)' : 'var(--border-w) solid var(--ink)',
        background: 'var(--paper)',
        boxShadow: plan.highlighted ? '10px 10px 0 0 var(--accent)' : 'var(--shadow)',
        padding: '24px 22px',
        display: 'flex',
        flexDirection: 'column',
        transform: plan.highlighted ? 'translateY(-8px)' : 'none'
      }}>
      {plan.highlighted && (
        <StripeTag variant="ink" side="right">
          Popular
        </StripeTag>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Pip bg={plan.highlighted ? 'var(--accent)' : 'var(--paper-2)'}>{plan.tier.toUpperCase()}</Pip>
      </div>

      <Text
        variant="h2"
        as="h3"
        style={{
          fontWeight: 900,
          letterSpacing: '-0.03em',
          margin: 0
        }}>
        {plan.name}
      </Text>

      <div style={{ margin: '10px 0 6px', display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <Text
          variant="h2"
          as="span"
          style={{
            fontWeight: 900,
            fontSize: 44,
            letterSpacing: '-0.03em',
            lineHeight: 1
          }}>
          {displayPrice}
        </Text>
        <Text variant="mono" as="span" opacity={0.7} style={{ fontSize: 12, letterSpacing: '0.1em' }}>
          {displayPeriodText}
        </Text>
      </div>

      <Text variant="body-sm" as="p" opacity={0.8} style={{ margin: '0 0 18px' }}>
        {plan.description}
      </Text>

      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: '0 0 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          flex: 1
        }}>
        {plan.features.map((feature) => (
          <Text
            key={feature}
            variant="body-sm"
            as="li"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              fontSize: 14
            }}>
            <Check />
            <span>{feature}</span>
          </Text>
        ))}
      </ul>

      <PricingPlanCta
        plan={plan}
        isCurrent={isCurrent}
        isFreePlanCard={isFreePlanCard}
        isDowngrade={Boolean(isDowngrade)}
        signUpMode={signUpMode}
        onSignUp={onSignUp}
        billingEnabled={billingEnabled}
        loading={loading}
        onSelect={onSelect}
      />
    </div>
  )
}

interface PricingPlanCtaProps {
  plan: PricingPlan
  isCurrent: boolean
  isFreePlanCard: boolean
  isDowngrade: boolean
  signUpMode: boolean
  onSignUp?: () => void
  billingEnabled: boolean
  loading: boolean
  onSelect: (tier: UserTier) => void
}

function PricingPlanCta({
  plan,
  isCurrent,
  isFreePlanCard,
  isDowngrade,
  signUpMode,
  onSignUp,
  billingEnabled,
  loading,
  onSelect
}: PricingPlanCtaProps) {
  const fullWidth = { width: '100%', justifyContent: 'center' } as const

  if (isCurrent) {
    return (
      <Button variant="default" disabled style={fullWidth}>
        Current plan
      </Button>
    )
  }
  if (signUpMode) {
    return (
      <Button variant={plan.highlighted ? 'primary' : 'default'} onClick={onSignUp} style={fullWidth}>
        {isFreePlanCard ? 'Get started' : plan.cta} →
      </Button>
    )
  }
  if (isDowngrade) {
    return (
      <Button variant="default" disabled={!billingEnabled} onClick={() => onSelect(plan.tier)} style={fullWidth}>
        {billingEnabled ? 'Downgrade' : 'Downgrade — soon'}
      </Button>
    )
  }
  if (billingEnabled) {
    return (
      <Button
        variant={plan.highlighted ? 'primary' : 'default'}
        onClick={() => onSelect(plan.tier)}
        disabled={loading}
        style={fullWidth}>
        {plan.cta} →
      </Button>
    )
  }
  return (
    <Button variant="default" disabled style={fullWidth}>
      Coming soon
    </Button>
  )
}
